/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import _ from 'lodash';
import React from 'react';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { toast } from 'react-toastify';

import {
  Button,
  Checkbox,
  DateInput,
  Form,
  Grid,
  Icon,
  Modal,
} from '@mairistem/composants';
import { SectionHeader } from '@mairistem/administres-utils';
import { request } from '@mairistem/tools';

import { API_DATE_FORMAT } from '@/constants';
import { ChampAdresse } from '@/components/Common';
import {
  buildContratMariageUpdatePayload,
  buildIndividuPutPayload,
  parseIndividuFiche,
} from '@/utils';

// Module autonome : propre `dayjs.extend`, ne pas dépendre d'un ordre d'import
// fortuit (cf. `validationInformationsNaissance`).
dayjs.extend(customParseFormat);

interface ModalContratMariageProps {
  open: boolean;
  /** Identifiant de l'acte : construit l'URL de `service.update`. */
  identifiant?: number;
  /** L'acte : existence et date du contrat. */
  acte?: any;
  /** Le NOTAIRE, individu du dossier : nom, prénom et commune de son office. */
  individu?: any;
  /** Endpoint générique des individus d'acte (`PUT {urlIndividus}/{id}`). */
  urlIndividus?: string;
  service?: { update: (data: Record<string, any>) => Promise<any> };
  onClose: () => void;
  onSaved: (acte: any) => void;
  /** Rafraîchit le notaire après son écriture. */
  onIndividuSaved?: (individu: any) => void;
}

/**
 * Panneau « CONTRAT DE MARIAGE » en édition : existence et date du contrat, et
 * le notaire qui l'a reçu.
 *
 * Deux tables, donc deux appels :
 *
 * - le NOTAIRE est un individu du dossier (rôle `NOT`) →
 *   `PUT /acte-individus/{id}` ;
 * - l'existence et la date sont sur l'acte →
 *   `PUT /numerisation-actes/mariage/{id}`.
 *
 * ⚠️ On écrit l'INDIVIDU d'abord, puis l'ACTE dans son `.then` — jamais en
 * parallèle (cf. `CLAUDE.md`, `request` n'est pas une `Promise`). L'ordre rend
 * aussi l'échec lisible : le premier appel a abouti, l'erreur est explicite.
 *
 * ⚠️ `am_cm_notair` reste vide : le client historique y stocke une phrase mise en
 * forme (« Me Albert PORCEL, Notaire à LA SEYNE (Var) ») que la numérisation ne
 * fabrique pas. Ce sont les données structurées du notaire qui sont écrites.
 */
const CHAMPS_NOTAIRE = ['nomFamille', 'prenom', 'adresseDomicile'];

const ModalContratMariage = (props: ModalContratMariageProps) => {
  const {
    open,
    identifiant,
    acte,
    individu,
    urlIndividus,
    service,
    onClose,
    onSaved,
    onIndividuSaved,
  } = props;

  const [data, setData] = React.useState<Record<string, any>>({});
  const [saving, setSaving] = React.useState(false);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    setData({
      contratMariage: Boolean(acte?.contratMariageExiste),
      dateContratMariage: acte?.contratMariageDate,
      ..._.pick(parseIndividuFiche(individu), CHAMPS_NOTAIRE),
    });
  }, [open, acte, individu]);

  const handleChange = React.useCallback(
    (
      _event: React.SyntheticEvent | null,
      payload: { name: string; value: unknown },
    ) => {
      const { name, value } = payload;
      setData((prev) => ({ ...prev, [name]: value }));
    },
    [],
  );

  const handleDateChange = React.useCallback(
    (date: Date | string | undefined) => {
      handleChange(null, {
        name: 'dateContratMariage',
        value: _.isNil(date) ? null : dayjs(date).format(API_DATE_FORMAT),
      });
    },
    [handleChange],
  );

  const handleContratChange = React.useCallback(
    (
      event: React.SyntheticEvent | null,
      { checked }: { checked?: boolean },
    ) => {
      handleChange(event, { name: 'contratMariage', value: Boolean(checked) });
    },
    [handleChange],
  );

  const existe = Boolean(data?.contratMariage);

  /** Enregistre le contrat sur l'acte, et referme. */
  const enregistrerActe = React.useCallback(() => {
    const payload = buildContratMariageUpdatePayload(
      identifiant as number,
      data,
    );

    const enregistrement = service?.update(payload);

    // Pas de service : le notaire est déjà écrit, on referme sans mentir.
    if (!enregistrement) {
      setSaving(false);
      onClose();
      return;
    }

    enregistrement
      .then(() => {
        onSaved({ ...acte, ...payload.acte });
        onClose();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement du contrat",
        );
      })
      .finally(() => {
        setSaving(false);
      });
  }, [service, identifiant, acte, data, onSaved, onClose]);

  const handleSave = React.useCallback(() => {
    setSaving(true);

    /*
     * Rien à écrire sur le notaire : soit il n'existe pas en base, soit le
     * contrat vient d'être décoché — on ne touche pas à son individu pour
     * autant, l'effacer déborderait de ce panneau.
     */
    if (!urlIndividus || _.isNil(individu?.identifiant) || !existe) {
      enregistrerActe();
      return;
    }

    const corps = buildIndividuPutPayload(
      individu,
      _.pick(data, CHAMPS_NOTAIRE),
    );

    request
      .put(`${urlIndividus}/${individu.identifiant}`, corps)
      .then(() => {
        onIndividuSaved?.({ ...individu, ...corps });
        enregistrerActe();
      })
      .catch(() => {
        toast.error(
          "Une erreur est survenue lors de l'enregistrement du notaire",
        );
        setSaving(false);
      });
  }, [urlIndividus, individu, existe, data, onIndividuSaved, enregistrerActe]);

  /*
   * `size="large"` parce que cette modale contient un `ChampAdresse` : en
   * `small`, sa ligne de voirie écrase ses champs. Le critère est la présence du
   * composant, pas le type d'acte.
   */
  return (
    <Modal open={open} onClose={onClose} size="large">
      <Modal.Header content="Modifier le contrat de mariage" />
      <Modal.Content>
        {/* @ts-ignore */}
        <Form>
          <Grid>
            <Grid.Row style={{ paddingLeft: '1em' }}>
              <Grid.Column computer={8} tablet={16} mobile={16}>
                <Form.Field>
                  {/* @ts-ignore */}
                  <Checkbox
                    label="Signature d'un contrat"
                    name="contratMariage"
                    checked={existe}
                    onChange={handleContratChange}
                  />
                </Form.Field>
              </Grid.Column>
              {existe && (
                <Grid.Column computer={8} tablet={16} mobile={16}>
                  <Form.Field>
                    <label>Contrat rédigé le</label>
                    <DateInput
                      value={
                        data?.dateContratMariage
                          ? dayjs(
                              data.dateContratMariage,
                              API_DATE_FORMAT,
                            ).toDate()
                          : undefined
                      }
                      onChange={handleDateChange}
                    />
                  </Form.Field>
                </Grid.Column>
              )}
            </Grid.Row>
          </Grid>
          {existe && (
            <>
              {/* @ts-ignore */}
              <SectionHeader title="Notaire" required={false} />
              <Grid>
                <Grid.Row style={{ paddingLeft: '1em' }}>
                  <Grid.Column computer={8} tablet={8} mobile={16}>
                    <Form.Input
                      fluid
                      label="Nom"
                      name="nomFamille"
                      maxLength={50}
                      value={data?.nomFamille ?? ''}
                      onChange={handleChange}
                    />
                  </Grid.Column>
                  <Grid.Column computer={8} tablet={8} mobile={16}>
                    <Form.Input
                      fluid
                      label="Prénom"
                      name="prenom"
                      maxLength={100}
                      value={data?.prenom ?? ''}
                      onChange={handleChange}
                    />
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              {/*
                ⚠️ `ChampAdresse` et non `ChampLieu` : le domicile est rendu par
                `parseIndividuFiche` à la forme d'`AdresseForm`, et c'est cette
                même forme que `buildIndividuPutPayload` sait réécrire. La fiche
                n'affiche que le département et la commune, comme la maquette,
                mais la saisie fait l'aller-retour complet — même écart que le
                déclarant d'un acte de décès.
              */}
              <ChampAdresse
                data={data}
                onChange={handleChange}
                name="adresseDomicile"
                title="Office notarial"
              />
            </>
          )}
        </Form>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>
          <Icon name="close" />
          Annuler
        </Button>
        <Button
          positive
          loading={saving}
          disabled={saving}
          data-testid="contrat-mariage-enregistrer"
          onClick={handleSave}
        >
          <Icon name="check" />
          Enregistrer
        </Button>
      </Modal.Actions>
    </Modal>
  );
};

export default ModalContratMariage;
